import { useEffect, useMemo } from 'react';
import { X, Calendar, Clock, MapPin } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import DOMPurify from 'dompurify';
import { analytics } from '../lib/analytics';
import { useIsPhone } from '../hooks/useMediaQuery';
import { BottomSheet } from '../components/mobile/BottomSheet';
import type { CalendarEvent, Member } from '../types/calendar';

// Check if content appears to be HTML
function isHtmlContent(text: string): boolean {
  return /<[a-z][\s\S]*>/i.test(text);
}

// Sanitize HTML and configure allowed tags
function sanitizeHtml(html: string): string {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'b', 'i', 'u', 'a', 'ul', 'ol', 'li', 'div', 'span'],
    ALLOWED_ATTR: ['href', 'target', 'rel'],
  });
}

interface EventPreviewModalProps {
  event: CalendarEvent;
  onClose: () => void;
  formatTime: (date: Date | string, includeAmPm?: boolean) => string;
  /** All members who have this event (for shared events) */
  members?: Member[];
}

export function EventPreviewModal({ event, onClose, formatTime, members }: EventPreviewModalProps) {
  const isPhone = useIsPhone();
  
  // Use members prop if provided, otherwise fall back to event.member
  const displayMembers = members && members.length > 0 ? members : event.member ? [event.member] : [];
  
  // Track modal open time for analytics
  useEffect(() => {
    const openTime = Date.now();

    return () => {
      const viewDuration = Date.now() - openTime;
      analytics.calendarEventModalClosed(event.id, viewDuration);
    };
  }, [event.id]);

  // Handle Escape key to close (only for desktop, BottomSheet handles its own)
  useEffect(() => {
    if (isPhone) return; // BottomSheet has its own escape handler
    
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose, isPhone]);

  const startDate = parseISO(event.startTime);
  const endDate = parseISO(event.endTime);
  const formattedDate = format(startDate, 'EEEE, MMMM d, yyyy');

  // Process description - sanitize HTML or use plain text
  const descriptionContent = useMemo(() => {
    if (!event.description) return null;
    if (isHtmlContent(event.description)) {
      return { __html: sanitizeHtml(event.description) };
    }
    return null;
  }, [event.description]);

  const isHtmlDescription = descriptionContent !== null;

  const content = (
    <>
      {/* Modal Header */}
      <div className="flex items-start justify-between p-4 border-b border-border-default">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          {/* Member color indicator */}
          {event.member && (
            <div
              className="w-3 h-3 rounded-full flex-shrink-0"
              style={{ backgroundColor: event.member.color }}
            />
          )}
          <h2 className="text-lg font-semibold text-text-primary break-words">
            {event.title}
          </h2>
        </div>
        <button
          onClick={onClose}
          className="btn-icon flex-shrink-0 ml-2"
          aria-label="Close modal"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Modal Body */}
      <div className="p-4 space-y-4">
        {/* Date */}
        <div className="flex items-center gap-3 text-text-secondary">
          <Calendar className="w-5 h-5 flex-shrink-0" />
          <span>{formattedDate}</span>
        </div>

        {/* Time or All Day */}
        <div className="flex items-center gap-3 text-text-secondary">
          <Clock className="w-5 h-5 flex-shrink-0" />
          {event.allDay ? (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-soft-cyan text-text-primary">
              All Day
            </span>
          ) : (
            <span>
              {formatTime(startDate)} – {formatTime(endDate)}
            </span>
          )}
        </div>

        {/* Location */}
        {event.location && (
          <div className="flex items-start gap-3 text-text-secondary">
            <MapPin className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.location)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="break-words text-soft-cyan hover:underline"
            >
              {event.location}
            </a>
          </div>
        )}

        {/* Description */}
        {event.description && (
          <div className="pt-2 border-t border-border-default">
            {isHtmlDescription ? (
              <div
                className="text-text-secondary text-sm break-words prose prose-sm max-w-none [&_a]:text-soft-cyan [&_a]:underline"
                dangerouslySetInnerHTML={descriptionContent!}
              />
            ) : (
              <p className="text-text-secondary text-sm whitespace-pre-wrap break-words">
                {event.description}
              </p>
            )}
          </div>
        )}

        {/* Member/Calendar Source */}
        {displayMembers.length > 0 && (
          <div className="pt-2 border-t border-border-default">
            <div className="flex flex-wrap gap-3">
              {displayMembers.map((m) => (
                <div key={m.id} className="flex items-center gap-2">
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium flex-shrink-0"
                    style={{ backgroundColor: m.color }}
                  >
                    {m.name.charAt(0).toUpperCase()}
                  </div>
                  <span className="text-text-primary font-medium">
                    {displayMembers.length === 1 ? `${m.name}'s Calendar` : m.name}
                  </span>
                </div>
              ))}
            </div>
            {displayMembers.length > 1 && (
              <div className="text-xs text-text-secondary mt-2">
                Shared across {displayMembers.length} calendars
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );

  // Mobile: Bottom sheet
  if (isPhone) {
    return (
      <BottomSheet isOpen={true} onClose={onClose} maxHeight={75}>
        {content}
      </BottomSheet>
    );
  }

  // Desktop: Centered modal
  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-bg-surface rounded-xl shadow-lg w-full max-w-md max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {content}
      </div>
    </div>
  );
}
